import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, FileText, X, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["New Project", "Planning", "In Progress", "Completed"];

/**
 * Dialog that updates a project's status. When status = "Completed", forces
 * the user to provide a Completion Report Number and upload a PDF.
 *
 * Props:
 *  - open, onOpenChange
 *  - projectId
 *  - currentStatus
 *  - onUpdated(updatedProject)
 */
export default function ProjectStatusDialog({
  open,
  onOpenChange,
  projectId,
  currentStatus,
  onUpdated,
}) {
  const [status, setStatus] = useState(currentStatus || "New Project");
  const [reportNumber, setReportNumber] = useState("");
  const [file, setFile] = useState(null); // FileRef or null
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStatus(currentStatus || "New Project");
      setReportNumber("");
      setFile(null);
    }
  }, [open, currentStatus]);

  const handleFile = async (selected) => {
    if (!selected) return;
    if (!/\.pdf$/i.test(selected.name)) {
      toast.error("Only PDF files are allowed");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", selected);
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile({
        id: data.id,
        path: data.path,
        filename: data.filename,
        content_type: data.content_type,
        size: data.size,
      });
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const submit = async () => {
    if (status === "Completed") {
      if (!reportNumber.trim()) {
        toast.error("Completion Report Number is required");
        return;
      }
      if (!file) {
        toast.error("Please upload the Completion Report PDF");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { status };
      if (status === "Completed") {
        payload.completion_report_number = reportNumber.trim();
        payload.completion_report_file = file;
      }
      const { data } = await api.patch(`/biz-projects/${projectId}/status`, payload);
      toast.success("Project status updated");
      onOpenChange(false);
      onUpdated?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-950 border border-white/10 text-zinc-100 max-w-md"
        data-testid="project-status-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white" style={{ fontFamily: "Outfit" }}>
            Update Project Status
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs text-zinc-400">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger
                className="mt-1.5 bg-zinc-900 border-white/10 text-white"
                data-testid="project-status-select"
              >
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100">
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {status === "Completed" && (
            <div className="space-y-3 border-t border-white/5 pt-4">
              <div>
                <Label className="text-xs text-zinc-400">Completion Report Number</Label>
                <Input
                  value={reportNumber}
                  onChange={(e) => setReportNumber(e.target.value)}
                  className="mt-1.5 bg-zinc-900 border-white/10 text-white"
                  placeholder="e.g. CR-2026-001"
                  data-testid="project-status-report-number"
                />
              </div>

              <div>
                <Label className="text-xs text-zinc-400">Completion Report PDF</Label>
                {file ? (
                  <div
                    className="mt-1.5 flex items-center justify-between bg-zinc-900 border border-white/10 rounded-md px-3 py-2"
                    data-testid="project-status-file"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-zinc-400" />
                      <span className="text-sm text-zinc-200 truncate">{file.filename}</span>
                      <span className="text-xs text-zinc-500 shrink-0">
                        {file.size ? `${(file.size / 1024).toFixed(1)} KB` : ""}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="p-1 text-zinc-500 hover:text-red-400"
                      data-testid="project-status-file-remove"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="mt-1.5 w-full border-2 border-dashed border-white/15 hover:border-white/30 bg-zinc-950/50 rounded-lg p-4 flex flex-col items-center justify-center text-zinc-400 transition-colors"
                    data-testid="project-status-file-trigger"
                  >
                    {uploading ? (
                      <Loader2 className="w-5 h-5 animate-spin mb-1.5" />
                    ) : (
                      <Upload className="w-5 h-5 mb-1.5" strokeWidth={1.5} />
                    )}
                    <div className="text-sm text-zinc-300">
                      {uploading ? "Uploading…" : "Upload Completion Report (PDF)"}
                    </div>
                  </button>
                )}
                <input
                  ref={inputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="project-status-file-input"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-zinc-300 hover:text-white">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || uploading}
            className="bg-white text-black hover:bg-zinc-200 font-medium"
            data-testid="project-status-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
